import { useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { Dropdown } from '../../../components/dropdown/dropdown';
import './account-request-list.scss';
import { SelectForm } from '../../../components/select-form/select-form';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SearchDropdownOption } from '../../../components/dropdown/search-dropdown';
import { ApiError } from '../../../clients/axios-instance';
import { useAdmin } from '../../../hooks/use-admin-context';
import {
  AccountRequest,
  AccountSearchRequest,
  OnAccountStatus,
} from '../../../clients/quipli-service/payment/on-account/on-account';
import { Badge } from '../../../components/badge/badge';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { toast } from '../../../util/toasts';

export const AccountRequests = () => {
  const { searchAccountRequests, approveAccount, declineAccount } = useAPI(),
    admin = useAdmin();

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<AccountSearchRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      statusFilter: params.has('statusFilter') ? (params.get('statusFilter')! as OnAccountStatus) : undefined,
      requestedAfter: params.has('requestedAfter') ? DateTime.fromISO(params.get('requestedAfter')!) : undefined,
      requestedBefore: params.has('requestedBefore') ? DateTime.fromISO(params.get('requestedBefore')!) : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setIsTableDataLoading(true);
      searchAccountRequests(_request)
        .then(({ items, pagination }) => {
          setTablePagination(pagination);
          setTableData(items.map(buildDataRow));
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
  );

  const OnAccountStatusValues: SearchDropdownOption[] = [
    { label: 'Pending', value: OnAccountStatus.Pending },
    { label: 'Approved', value: OnAccountStatus.Approved },
    { label: 'Declined', value: OnAccountStatus.Declined },
  ];

  const columns: DataTableColumn[] = [
    { key: 'user', label: 'User details' },
    { key: 'account', label: 'Account No.' },
    { key: 'requestDate', label: 'Request date' },
    { key: 'approvedRejectedAt', label: 'Approved/Rejected' },
    { key: 'status', label: 'Status' },
    { key: 'actions', label: '', fixedAsLast: true },
  ];

  const buildDataRow = (account: AccountRequest) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {account.user.name}
          </Typography>
          <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
            {account.user.email}
          </Typography>
        </>
      ),
      fields: {
        user: (
          <>
            <div>
              <Typography type="text-sm" className="text-quipli-black-800">
                {account.user.name}
              </Typography>
              <Typography type="text-sm" className="text-quipli-black-800">
                {account.user.email}
              </Typography>
            </div>
          </>
        ),

        account: (
          <>
            <Typography type="text-sm" className="text-quipli-black-800">
              {account.accountNumber}
            </Typography>
          </>
        ),
        requestDate: (
          <>
            {account.requestedAt ? (
              <div>
                <Typography type="text-sm" className="text-quipli-black-800">
                  {account.requestedAt.toFormat('LLL-dd')}
                </Typography>
                <Typography type="text-xs" className="text-grey-500">
                  {account.requestedAt.toFormat('hh:mm a').toLowerCase()}
                </Typography>
              </div>
            ) : (
              <>
                <Typography type="text-sm" className="text-center text-quipli-black-800">
                  N/A
                </Typography>
              </>
            )}
          </>
        ),
        approvedRejectedAt: (
          <>
            {account.approvedAt && account.approvedBy ? (
              <div>
                <Typography type="text-sm" className="text-quipli-black-800">
                  {account.approvedAt.toFormat('LLL-dd, hh:mm a')}
                </Typography>
                <Typography type="text-xs" className="text-grey-500">
                  {account.approvedBy.adminFullName}
                </Typography>
              </div>
            ) : (
              <>
                <Typography type="text-sm" className="text-quipli-black-800">
                  N/A
                </Typography>
              </>
            )}
          </>
        ),
        status: (
          <>
            <Badge
              text={account.status}
              type={
                account.status === OnAccountStatus.Approved
                  ? 'success'
                  : account.status === OnAccountStatus.Declined
                  ? 'danger'
                  : 'default'
              }
            />
          </>
        ),
        actions: <Actions accountRequest={account} approveAccount={approve} declineAccount={decline} />,
      },
      redirectTo: `users/customer_form/${account.userId}/payment`,
    };
  };

  const approve = async (accountRequest: AccountRequest) => {
    approveAccount(accountRequest.userId, admin.id)
      .then(() => {
        fetchCallback();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error updating on account request';
        toast(errMsg, 'error');
      });
  };

  const decline = async (accountRequest: AccountRequest) => {
    declineAccount(accountRequest.userId, admin.id)
      .then(() => {
        fetchCallback();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error updating on account request';
        toast(errMsg, 'error');
      });
  };

  return (
    <div className="account-request-list container-v2">
      <div className="flex justify-between items-center">
        <Typography type="display-sm" weight="medium">
          Account Requests
        </Typography>
      </div>

      <div className="pt-10 pb-5 flex items-center justify-between">
        <SearchBar
          data-cy="search-renters"
          placeholder="Search by keyword"
          defaultValue={request.keyword}
          setRequest={setRequest}
        />
      </div>

      <div className="flex flex-wrap md:max-lg:grid md:max-lg:grid-cols-2 sm:max-md:grid sm:max-md:grid-cols-2 w-full items-center justify-start gap-2 mb-3 filters-wrapper">
        <div className="w-full md:w-auto">
          <SelectForm
            options={OnAccountStatusValues}
            value={request.statusFilter ? OnAccountStatusValues.find((v) => v.value === request.statusFilter) : null}
            onChange={(ps) => {
              setRequest((prevState) => ({ ...prevState, statusFilter: ps?.value as OnAccountStatus }));
            }}
            isClearable={true}
            placeholder="Status"
            isSearchable={false}
          />
        </div>
        <div className="w-full md:w-auto requested-dates">
          <DatePicker
            value={request.requestedAfter}
            onChange={(requestedAfter) => setRequest((prevState) => ({ ...prevState, requestedAfter }))}
            placeholder="Requested After"
            clearable
          />
        </div>
        <div className="w-full md:w-auto requested-dates">
          <DatePicker
            value={request.requestedBefore}
            onChange={(requestedBefore) => setRequest((prevState) => ({ ...prevState, requestedBefore }))}
            placeholder="Requested Before"
            clearable
          />
        </div>
      </div>

      <DataTable
        isLoading={isTableDataLoading}
        columnNames={columns}
        data={tableData}
        pagination={tablePagination}
        request={request}
        setRequest={setRequest}
        cypressPrevious="previous_renters_page"
        cypressNext="next_renters_page"
      />
    </div>
  );
};

const Actions = ({
  accountRequest,
  declineAccount,
  approveAccount,
}: {
  accountRequest: AccountRequest;
  declineAccount: (account: AccountRequest) => void;
  approveAccount: (account: AccountRequest) => void;
}) => {
  return (
    <div>
      {accountRequest.status === OnAccountStatus.Approved ? (
        <Button
          className="button-tertiary"
          onClick={() => {
            declineAccount(accountRequest);
          }}
        >
          <Icon icon="XCircle" />
        </Button>
      ) : (
        <>
          {accountRequest.status === OnAccountStatus.Declined ? (
            <Button
              className="button-tertiary"
              onClick={() => {
                approveAccount(accountRequest);
              }}
            >
              <Icon icon="CheckCircle" stroke="var(--success-500)" />
            </Button>
          ) : (
            <Dropdown
              placement="left-end"
              trigger={() => (
                <Button className="button-tertiary">
                  <Icon icon="DotsVertical" />
                </Button>
              )}
            >
              <div
                className="flex gap-2"
                onClick={() => {
                  approveAccount(accountRequest);
                }}
              >
                <Icon icon="CheckCircle" stroke="var(--success-500)" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Approve request
                </Typography>
              </div>

              <div
                className="flex gap-2"
                onClick={() => {
                  declineAccount(accountRequest);
                }}
              >
                <Icon icon="XCircle" stroke="var(--error-500)" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Reject request
                </Typography>
              </div>
            </Dropdown>
          )}
        </>
      )}
    </div>
  );
};
